 'use client';
 import { useState } from 'react';
 import Link from 'next/link';
 import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { signOut } from 'next-auth/react';
import {
  User,
  Workflow,
  Banknote,
  Mail,
  MapPin,
  ClipboardCheck,
  Menu,
  X,
  Shield,
  DollarSign,
  FileText,
  Building2,
} from 'lucide-react';
 import { cn } from '@/lib/utils';
import Image from 'next/image';
import { useIsMobile } from '@/hooks/use-mobile';

 export const AdminSidebar = () => {
   const pathname = usePathname();
   const [isOpen, setIsOpen] = useState(false);
   const isMobile = useIsMobile();

   const toggleSidebar = () => setIsOpen(!isOpen);

  const navItems = [
    { name: 'Analytics', path: '/admin/analytics', icon: <Workflow size={20} /> },
    { name: 'Stripe Monitor', path: '/admin/stripe', icon: <Banknote size={20} /> },
    { name: 'Monitoring', path: '/admin/monitoring', icon: <Shield size={20} /> },
    { name: 'Communications', path: '/admin/communications', icon: <Mail size={20} /> },
    { name: 'AI Brand', path: '/admin/ai-brand', icon: <Shield size={20} /> },
    { name: 'Approvals', path: '/admin/approvals', icon: <Shield size={20} /> },
    { name: 'Disputes', path: '/admin/disputes', icon: <Shield size={20} /> },
    { name: 'Exports', path: '/admin/exports', icon: <FileText size={20} /> },
    { name: 'Driver Compliance', path: '/admin/driver-compliance', icon: <ClipboardCheck size={20} /> },
    { name: 'SSO & SCIM', path: '/admin/sso', icon: <Shield size={20} /> },
    { name: 'i18n Demo', path: '/admin/i18n-demo', icon: <FileText size={20} /> },
    { name: 'Privacy (DPA/SAR)', path: '/admin/privacy', icon: <Shield size={20} /> },
    { name: 'Refunds', path: '/admin/refunds', icon: <Banknote size={20} /> },
    { name: 'Support', path: '/admin/support', icon: <Shield size={20} /> },
    { name: 'Broadcast', path: '/admin/notifications', icon: <Mail size={20} /> },
    { name: 'Job Chats', path: '/admin/chats', icon: <Mail size={20} /> },
    { name: 'Demo Seed', path: '/admin/demo-seed', icon: <Workflow size={20} /> },
    { name: 'Mobile Logs', path: '/admin/logs', icon: <FileText size={20} /> },
    { name: 'Test Page', path: '/admin/test', icon: <Workflow size={20} /> },
    // Removed CMS/blog from admin
    // { name: 'Devices', path: '/admin/devices', icon: <MapPin size={20} /> },
    { name: 'Payouts', path: '/admin/payouts', icon: <DollarSign size={20} /> },
    { name: 'Webhooks', path: '/admin/webhooks', icon: <Shield size={20} /> },
    { name: 'All Users', path: '/admin/users', icon: <User size={20} /> },
    { name: 'All Jobs', path: '/admin/jobs', icon: <Workflow size={20} /> },
    { name: 'Post Job', path: '/admin/post-job', icon: <Workflow size={20} /> },
    { name: 'All Quotes', path: '/admin/quotes', icon: <Banknote size={20} /> },
    { name: 'Fund Management', path: '/admin/funds', icon: <DollarSign size={20} /> },
    { name: 'Withdrawal Requests', path: '/admin/withdrawl-requests', icon: <Shield size={20} /> },
    { name: 'Contact Entries', path: '/admin/entries', icon: <Mail size={20} /> },
    { name: 'Driver Verifications', path: '/admin/driver-verifications', icon: <ClipboardCheck size={20} /> },
    { name: 'User Locations', path: '/admin/locations', icon: <MapPin size={20} /> },
    { name: 'Fleet Businesses', path: '/admin/fleet-businesses', icon: <Building2 size={20} /> },
  ];

   const closeSidebarIfMobile = () => {
     if (isMobile) setIsOpen(false);
   };

   return (
     <>
      <div className="fixed z-10 top-4 left-4 md:hidden">
        <Button variant="outline" size="icon" onClick={toggleSidebar} className="bg-background">
           {isOpen ? <X size={20} /> : <Menu size={20} />}
         </Button>
       </div>
       {isOpen && isMobile && (
         <div className="fixed inset-0 bg-black/20 z-20" onClick={() => setIsOpen(false)} />
       )}

      <div
        className={cn(
          'fixed top-0 bottom-0 left-0 z-30 w-64 bg-sidebar border-r border-border p-6 transition-transform duration-300 ease-in-out md:translate-x-0',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
          <div className="flex flex-col h-full">
          <div className="flex flex-col items-center justify-center mb-8">
            <Image
              src="/logo.svg"
              alt="Smart Taurus Logo"
              width={150}
              height={50}
              className="object-contain"
            />
            <h1 className="text-2xl font-bold text-primary mt-4">Admin Panel</h1>
          </div>
          <nav className="space-y-2 overflow-y-auto pr-2" style={{ maxHeight: 'calc(100vh - 220px)' }}>
             {navItems.map((item) => (
               <Link
                 key={item.name}
                 href={item.path}
                 onClick={closeSidebarIfMobile}
                 className={cn('nav-link', pathname === item.path && 'active')}
               >
                 {item.icon}
                 <span>{item.name}</span>
               </Link>
             ))}
           </nav>
           <div className="mt-auto pt-6 border-t">
          <div className="flex items-center space-x-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary font-medium">
              <User size={18} />
            </div>
            <div>
              <p className="font-medium">Admin Account</p>
              <p className="text-sm text-black">Manage platform settings</p>
            </div>
          </div>
          <div className="mt-4">
            <Button variant="destructive" size="sm" className="w-full" onClick={() => signOut({ callbackUrl: '/login' })}>
              Logout
            </Button>
          </div>
           </div>
         </div>
       </div>
       <div className="hidden md:block w-64" />
     </>
   );
 };

 export default AdminSidebar;